import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProducts } from '../contexts/ProductsContext';
import { useCart } from '../contexts/CartContext';
import { useInventory } from '../contexts/InventoryContext';
import { useToast } from '../contexts/ToastContext';
import { formatStockMessage, getCartQuantity } from '../utils/stockUtils';
import Breadcrumbs from '../components/Breadcrumbs/Breadcrumbs';
import './ProductDetailPage.css';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { getProductById, getProductByIdOrFetch } = useProducts();
  const { addToCart, cartItems } = useCart();
  const { loadInventory, getStockForSize } = useInventory();
  const { success, error: toastError } = useToast();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const fromList = getProductById(productId);
      if (fromList) {
        if (!cancelled) setProduct(fromList);
        if (!cancelled) setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const p = await getProductByIdOrFetch(productId);
        if (!cancelled) setProduct(p);
      } catch (_) {
        if (!cancelled) setProduct(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [productId, getProductById, getProductByIdOrFetch]);

  // Carrega estoque quando produto estiver disponível
  useEffect(() => {
    if (product?.id) {
      loadInventory(product.id, {
        sizesAvailable: product.sizesAvailable || ['P', 'M', 'G', 'GG'],
        name: product.name,
        price: product.price,
      });
    }
  }, [product, loadInventory]);

  useEffect(() => {
    setSelectedSize(null);
    setSelectedImageIndex(0);
    setAddedToCart(false);
  }, [productId]);

  const handleAddToCart = () => {
    if (!product || !selectedSize) return;
    const stock = getStockForSize(product.id, selectedSize);
    const cartQty = getCartQuantity(cartItems, product.id, selectedSize);

    if (stock <= 0) {
      toastError(`Tamanho ${selectedSize} esgotado.`);
      return;
    }

    if (cartQty >= stock) {
      toastError(formatStockMessage(stock, selectedSize));
      return;
    }

    addToCart({
      ...product,
      thumbnail: product.images?.[0],
      stripePriceId: product.priceId,
      size: selectedSize,
    });
    setAddedToCart(true);
    success('Adicionado ao carrinho!');
    setTimeout(() => setAddedToCart(false), 3000);
  };

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="product-detail-loading">Carregando...</div>
      </div>
    );
  }

  if (!product) {
    navigate('/catalog');
    return null;
  }

  const images = product.images?.length ? product.images : [null];
  const sizesAvailable = product.sizesAvailable?.length ? product.sizesAvailable : ['P', 'M', 'G', 'GG'];

  return (
    <div className="product-detail-page">
      <Breadcrumbs items={[{ label: 'Início', to: '/' }, { label: 'Catálogo', to: '/catalog' }, product.name]} />
      <div className="product-detail-container">
        <div className="product-detail-gallery">
          <div className="product-detail-main-image">
            {images[selectedImageIndex] ? (
              <img
                src={images[selectedImageIndex]}
                alt={`${product.name} - imagem ${selectedImageIndex + 1}`}
                loading="lazy"
              />
            ) : (
              <div className="product-detail-image-placeholder">HAKA</div>
            )}
          </div>
          {images.length > 1 && (
            <div className="product-detail-thumbnails">
              {images.map((img, index) => (
                <button
                  key={index}
                  type="button"
                  className={`product-detail-thumb ${selectedImageIndex === index ? 'active' : ''}`}
                  onClick={() => setSelectedImageIndex(index)}
                >
                  {img ? <img src={img} alt="" loading="lazy" /> : <span>HAKA</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="product-detail-info">
          <h1 className="product-detail-title">{product.name}</h1>
          {product.description && (
            <p className="product-detail-description">{product.description}</p>
          )}
          <p className="product-detail-price">{product.price}</p>
          <div className="product-detail-sizes">
            <span className="product-detail-sizes-label">Tamanho:</span>
            <div className="product-detail-size-options">
              {sizesAvailable.map((size) => {
                const stock = getStockForSize(product.id, size);
                const outOfStock = stock <= 0;
                return (
                  <button
                    key={size}
                    type="button"
                    className={`product-detail-size-btn ${selectedSize === size ? 'active' : ''} ${outOfStock ? 'out-of-stock' : ''}`}
                    onClick={() => !outOfStock && setSelectedSize(size)}
                    disabled={outOfStock}
                    title={outOfStock ? 'Esgotado' : `${stock} disponíveis`}
                  >
                    {size}
                    {outOfStock && <span className="size-esgotado-label">Esgotado</span>}
                  </button>
                );
              })}
            </div>
          </div>
          {selectedSize && (() => {
            const stock = getStockForSize(product.id, selectedSize);
            const cartQty = getCartQuantity(cartItems, product.id, selectedSize);
            const remaining = stock - cartQty;

            let message = null;
            if (stock <= 0) return null;
            if (remaining <= 0) {
              message = `Você já tem ${cartQty} no carrinho — estoque esgotado para este tamanho.`;
            } else if (stock === 1) {
              message = 'Última unidade disponível!';
            } else if (stock <= 3) {
              message = `Apenas ${stock} disponíveis${cartQty > 0 ? ` (${remaining} restantes para você)` : ''}`;
            }

            if (!message) return null;
            return <p className="product-detail-stock-hint">{message}</p>;
          })()}
          {addedToCart ? (
            <div className="product-detail-added-msg">
              ✓ Adicionado ao carrinho!{' '}
              <button
                type="button"
                className="product-detail-go-checkout"
                onClick={() => navigate('/checkout')}
              >
                Finalizar compra
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-filled product-detail-add-btn"
              disabled={!selectedSize}
              onClick={handleAddToCart}
            >
              <span>{selectedSize ? 'Adicionar ao carrinho' : 'Selecione um tamanho'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
